import React, { useState, useCallback, useEffect } from "react";
import ReactFlow, {
    Controls,
    Background,
    addEdge,
    useNodesState,
    useEdgesState,
    Handle,
    Position,
} from "reactflow";
import "reactflow/dist/style.css";
import log from "loglevel";

// Custom Node ใช้รูปภาพ
const ImageNode = ({ data }) => {
    return (
        <div style={{ padding: 3, border: `3px solid ${data.expanded == true ? '#ddd' : 'red'}`, borderRadius: 5, background: '#fff' }} className="relative">
            <Handle type="target" position={Position.Top} />
            <img src={data.image} alt={data.text} className="w-20 h-20 rounded-full shadow-lg" style={{ display: data.image ? "block" : "none" }} />
            <div style={{ textAlign: 'center', marginTop: '1px', display: data.text ? "block" : "none" }}>{data.text}</div>
            <Handle type="source" position={Position.Bottom} />
        </div>
    );
};

// กำหนด Custom Node
const nodeTypes = { imageNode: ImageNode };

const Y_SPACE_BETWEEN_LINE = process.env.Y_SPACE_BETWEEN_LINE || 55;
const X_SPACE_BETWEEN_DEPTH = process.env.X_SPACE_BETWEEN_DEPTH || 75;

let nodeData = {};

/**
 * @param {string} quizFile 
 * @param {string} nodeId 
 * @param {string} text 
 * @returns 
 **/
function buildMindMapNode(quizFile, nodeId, text) {
    let nodeData = {};

    if (text.startsWith("i=")) {
        text = text.replace("i=", "");
        log.debug(`image=${text}`);
        nodeData = { image: `/images/${quizFile}/${text}`, expanded: false }
    }
    else if (text.startsWith("t=")) {
        text = text.replace("t=", "");
        log.debug(`text=${text}`)
        nodeId = text;
        nodeData = { text: `${text}`, expanded: false }
    }

    return {
        id: nodeId,
        type: "imageNode",
        position: { x: 0, y: 0 },
        data: nodeData
    };
}

function parseMindMapData(quizFile, lines) {
    const stack = [];
    const tree = {};
    let lineNo = 0;

    lines.forEach(line => {
        let nodeId = "" + lineNo;

        const depth = line.length - line.trimStart().length; // นับ tab เพื่อหาความลึก

        let parts = line.trim().split("|");
        let nextPart = parts.slice(1);

        const childNode = buildMindMapNode(quizFile, nodeId, nextPart.toString());
        tree[childNode.id] = {
            self: childNode,
            children: []
        };

        while (stack.length > depth) stack.pop(); // ปรับระดับ stack ตามความลึก

        if (stack.length) {
            const parentNodeId = stack[stack.length - 1].id;

            childNode.position.y = (lineNo * Y_SPACE_BETWEEN_LINE);
            childNode.position.x = (depth * X_SPACE_BETWEEN_DEPTH);
            tree[parentNodeId].children.push(childNode);
        }

        stack.push(childNode);
        lineNo++;
    });

    return tree;
}


export default function QuizMindMap({ quizFile, mindMapFile }) {
    // ----- Log Level Configuration -----
    log.setLevel(process.env.REACT_APP_LOG_LEVEL || "info");

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // ----- Screen size handler -----
    const [modalSize, setModalSize] = useState({ width: "50vw", height: "50vh" });

    // Function to update modal size based on screen size
    const updateModalSize = () => {
        const width = Math.min(window.innerWidth * 0.7, 2000); // 70% of screen width, max 2000px
        const height = Math.min(window.innerHeight * 0.8, 1400); // 60% of screen height, max 1400px
        setModalSize({ width: `${width}px`, height: `${height}px` });
    };

    // Run once on mount & update on resize
    useEffect(() => {
        updateModalSize();
        window.addEventListener("resize", updateModalSize);
        return () => window.removeEventListener("resize", updateModalSize);
    }, []);
    // ----- Screen size handler -----

    // ----- Mind Map -----
    log.info(`Quiz file: ${quizFile}, Mind map file: ${mindMapFile}`)

    let mindMapPaths = mindMapFile.split("?");
    mindMapFile = mindMapPaths[0].toString().trim();
    log.info(`mindMapFile splited: ${mindMapFile}, length: ${mindMapPaths.length}`);

    useEffect(() => {
        fetch(process.env.PUBLIC_URL + "/images/" + quizFile + "/" + mindMapFile)
            .then((response) => response.text())
            .then((text) => {
                let lines = text.trim().split("\n");
                nodeData = parseMindMapData(quizFile, lines);
                log.info("Node Data:", JSON.stringify(nodeData, null, 2));

                // ดึง key แรก
                const rootKey = Object.keys(nodeData)[0];
                let rootNode = mindMapPaths.length == 1
                    ? nodeData[rootKey]
                    : nodeData[mindMapPaths[1].toString().trim()];

                // rootNode = nodeData["อาหาร"];

                setNodes([rootNode.self]);

                // log.info("Node Data:", JSON.stringify(nodeData, null, 2));
            })
            .catch((error) => log.error("Error loading the file:", error));
    }, []);

    const onNodeClick = useCallback((event, node) => {
        log.debug(`Node ${node.id} is clicked`);
        buildChildNode(node);
    }, [nodes]);

    /**
     * 
     * @param {Mind map node} node 
     * @returns 
     * 
     */
    function buildChildNode(node) {
        // = if nodeData[node.id] is NULL หรือ undefined, return an empty array []
        const children = nodeData[node.id].children || [];

        // ถ้า node ที่ถูก click ไม่มี children ไม่ต้องทำอะไร เช่น nodeData["5"] ไม่มีข้อมูล children
        if (!children || children.length === 0) {
            node.data.expanded = true;
            return;
        };

        // หา node ที่ไม่มีอยู่ใน nodes เดิม แล้วคืนค่า ตัวแรกที่หาเจอ
        // i.e. ถ้า nodes มี [1, 2, 3] และ children มี [1, 2, 3, 4, 5] คืนค่า 4
        const nextNode = children.find(n => !nodes.find(existing => existing.id === n.id));
        if (!nextNode) return;

        const nextNodeChildren = nodeData[nextNode.id] ? nodeData[nextNode.id].children : [] || [];
        const nextNodeExpanded = nextNodeChildren.length == 0 ? true : false;
        log.debug(`has children: ${nextNodeChildren.length}, expanded: ${nextNodeExpanded}`);
        nextNode.data.expanded = nextNodeExpanded;


        setNodes((nds) => [
            // คัดลอก nodes ปัจจุบันที่มีอยู่
            ...nds,

            // และเพิ่ม node ใหม่
            {
                id: nextNode.id,
                type: "imageNode",
                position: nextNode.position,
                data: nextNode.data
            }
        ]);

        setEdges((eds) => [
            // คัดลอก edges ปัจจุบันที่มีอยู่
            ...eds,

            // และเพิ่ม edge ใหม่
            {
                id: `e${node.id}-${nextNode.id}`,
                source: node.id,
                target: nextNode.id
            }]
        );

        const totalChildrenExpanded = edges.filter(item => item.source === node.id).length + 1;
        if (totalChildrenExpanded == children.length) {
            node.data.expanded = true;
        }

        log.info(`nodes: ${JSON.stringify(nodes, null, 2)}`);
        log.info(`edges: ${JSON.stringify(edges, null, 2)}`);
    }


    return (
        <div
            style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "white",
                padding: "16px",
                borderRadius: "8px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                width: modalSize.width,  // Custom width
                height: modalSize.height, // Custom height
            }}
        >

            <div style={{ height: modalSize.height }}>
                {/* React flow */}
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={onNodeClick}
                    nodeTypes={nodeTypes} // ใช้ Custom Node
                    defaultViewport={{ x: 0, y: 0, zoom: 0.8 }} 
                    // minZoom={0.1}
                >
                    <Controls />
                    <Background />
                </ReactFlow>
            </div>
        </div>
    );
}
